import { createContext, useContext, useEffect } from 'react'
import { ThemeContext as AppThemeContext } from '../../contexts/ThemeContext'
import { clearColorCache } from '../../map-editor-features/viewer/canvas/colorTokens'
import { getActiveCanvasRenderer } from '../../map-editor-features/viewer/canvas/activeRenderer'

type Theme = 'light' | 'dark'

interface ThemeContextType {
    theme: Theme
    toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    // 메인 앱(App.tsx)의 ThemeContext 를 단일 소스로 사용한다.
    // 과거에는 별도 useState 로 localStorage.themeMode 를 마운트 시 1회만 읽어,
    // 상단 공통 헤더(메인 앱)에서 테마를 토글해도 맵 에디터 본문의 data-theme 가
    // 갱신되지 않아 "헤더만 바뀌고 본문은 그대로"인 버그가 있었다.
    // 이제 메인 앱 mode 를 그대로 따라가므로 한 번의 토글로 헤더·본문이 함께 전환된다.
    const { mode, toggleTheme } = useContext(AppThemeContext)
    const theme: Theme = mode

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme)
        // Canvas 는 var(--color-map-*) 를 probe 로 1회 해석해 캐시한다 —
        // 비우지 않으면 토글 후에도 이전 테마 색을 계속 칠한다.
        clearColorCache()
        getActiveCanvasRenderer()?.render()
    }, [theme])

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    )
}

export function useTheme() {
    const context = useContext(ThemeContext)
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
}
